import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { OverlayRef } from '@angular/cdk/overlay';
import { OVERLAY_REF, UtilsService } from '../../shared/utils.service';
import { ApiDataSource } from '../../utils/api-data-source';
import { ApiRequestService } from '../../shared/api-request.service';
import { AppService } from '../../app.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { StorageService } from '../../shared/storage.service';
import { RBACService } from '../../shared/rbac.service';
import { Router } from '@angular/router';
import { SentryService } from '../../services/sentry.service';
import { AppcuesService } from '../../services/appcues.service';
import { Location } from '@angular/common';

@Component({
  selector: 'app-account-switcher',
  templateUrl: './account-switcher.component.html',
  styleUrls: ['./account-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountSwitcherComponent implements OnInit, OnDestroy {
  dataSource: AccountSwitcherDataSource = new AccountSwitcherDataSource(
    this.app,
    this.api
  );

  private searchSubject: Subject<string> = new Subject<string>();

  constructor(
    @Inject(OVERLAY_REF) public overlayRef: OverlayRef,
    private api: ApiRequestService,
    public app: AppService,
    private utils: UtilsService,
    private storage: StorageService,
    private rbac: RBACService,
    private router: Router,
    private sentry: SentryService,
    private appcuesService: AppcuesService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.dataSource.getData();

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(
        (): void => {
          this.dataSource.getData(true);
        }
      );
  }

  onSearch(): void {
    this.searchSubject.next(this.dataSource.search);
  }

  /**
   * The accounts to display in the switcher.
   *
   * Two exclusions:
   *
   *  - The currently active account. There is no value in switching to the account you are already on.
   *  - The user's own personal account. It is not a place to work: it exists so that a user always has an
   *    approved membership somewhere, which is what keeps them able to sign in while a request to join a
   *    company is still pending (SC-301). It holds no sites, no employees and no company content, so
   *    offering it as a destination in a switcher listing real companies is noise.
   *
   * Filtered client side with AppService.isPersonalAccount(), matching how the employer selector already
   * drops it in GlobalAccountsSelectorComponent.getMyAccounts(). Deliberately NOT filtered in
   * v1/managed-accounts: that endpoint also backs the employer selector and the PUT that performs the
   * switch, so narrowing it would reach further than this screen.
   *
   * isPersonalAccount() tests specifically for the SIGNED-IN user's own personal account, not for personal
   * accounts in general, so a super admin who has been granted access to somebody else's personal account
   * can still switch into it for support work.
   *
   * @returns {any[]} The switchable accounts.
   */
  getVisibleAccounts(): any[] {
    return this.dataSource.dataSubject
      .getValue()
      .filter((record: any): boolean =>
        record.id !== this.app.account.id && !this.app.isPersonalAccount(record)
      );
  }

  /**
   *
   * Similar code in sc-web-portal/src/app/admin/accounts/accounts.component.ts
   * Similar code in sc-web-portal/src/app/shared/auth.service.ts
   * Similar code in sc-web-portal/src/app/top-nav/top-nav.component.ts
   *
   * @param account_id The account id to switch to.
   */
  onSwitchAccount(account_id: number): void {
    this.api
      .makeRequest('put', 'v1/managed-accounts', {
        id: account_id
      })
      .then(
        (response: any): void => {
          this.storage.set('AuthToken', response.data.api_token);
          this.storage.set('Permissions', response.data.permissions);

          this.app.user.apply(response.data.user);
          this.app.account.apply(response.data.account);
          this.app.userAccountLink.apply(response.data.user_account_link);

          // Apply permissions last: userCanAccess() also reads app.user.is_sitesoft_admin, and applying
          // permissions emits the change notification that components use to recompute their flags.
          this.rbac.applyPermissions(response.data.permissions);

          this.sentry.setActiveAccount(this.app.account.id);

          this.appcuesService.onIdentifyGroup(this.app.account.name, {
            name: this.app.account.name,
            industry: this.app.account.industry,
            address: this.app.account.address,
            suburb: this.app.account.suburb,
            city: this.app.account.city,
            postal_code: this.app.account.postal_code,
            country: this.app.account.country
          });

          this.app.quickAccessSession =
            response.data.quick_access_session || null;

          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigateByUrl(this.location.path());
            });

          this.utils.showToast(
            `You successfully switched to ${this.app.account.name}.`
          );

          this.overlayRef.dispose();
        }
      );
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }
}

export class AccountSwitcherDataSource extends ApiDataSource {
  search: string = '';
  order_by: string = 'name';
  order: string = 'asc';
  limit: number = 30;

  getData(resetOffset: boolean = false): void {
    this.makeRequest('v1/managed-accounts', resetOffset, {
      search: this.search,
      order: this.order,
      order_by: this.order_by
    });
  }
}
